import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import { Link, type LinkProps } from "react-router-dom";
import clsx from "clsx";

import styles from "./button.module.css";

export type ButtonVariant =
  | "muted-outline"
  | "light-grey"
  | "accent"
  | "success"
  | "danger"
  | "accent-ghost"
  | "danger-ghost"
  | "warning"
  | "link";

function variantClass(variant: ButtonVariant): string {
  switch (variant) {
    case "muted-outline":
      return styles.mutedOutline;
    case "light-grey":
      return styles.lightGrey;
    case "danger":
      return styles.danger;
    case "success":
      return styles.success;
    case "accent-ghost":
      return styles.accentGhost;
    case "danger-ghost":
      return styles.dangerGhost;
    case "link":
      return styles.link;
    case "warning":
      return styles.warning;
    case "accent":
    default:
      return styles.accent;
  }
}

export interface ButtonProps extends ComponentPropsWithoutRef<"button"> {
  variant?: ButtonVariant;
  /** Compact control for toolbars and dense headers. */
  size?: "md" | "sm";
  fullWidth?: boolean;
  /** Use start alignment for full-width menu-style actions. */
  align?: "center" | "start";
  startIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = "accent",
    size = "md",
    fullWidth = false,
    align = "center",
    startIcon,
    children,
    type = "button",
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={clsx(
        styles.root,
        variantClass(variant),
        size === "sm" && styles.sizeSm,
        fullWidth && styles.fullWidth,
        align === "start" && styles.justifyStart,
        className,
      )}
      {...props}
    >
      {startIcon != null ? <span className={styles.icon}>{startIcon}</span> : null}
      {children}
    </button>
  );
});

Button.displayName = "Button";

export type ButtonLinkProps = Omit<LinkProps, "className"> &
  Pick<
    ButtonProps,
    "variant" | "size" | "fullWidth" | "align" | "startIcon" | "className"
  >;

export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  function ButtonLink(
    {
      className,
      variant = "accent",
      size = "md",
      fullWidth = false,
      align = "center",
      startIcon,
      children,
      ...linkProps
    },
    ref,
  ) {
    return (
      <Link
        ref={ref}
        className={clsx(
          styles.root,
          variantClass(variant),
          size === "sm" && styles.sizeSm,
          fullWidth && styles.fullWidth,
          align === "start" && styles.justifyStart,
          className,
        )}
        {...linkProps}
      >
        {startIcon != null ? <span className={styles.icon}>{startIcon}</span> : null}
        {children}
      </Link>
    );
  },
);

ButtonLink.displayName = "ButtonLink";
